/**
 * Helper functions for chart-related operations
 */

/**
 * Parse selections from currentValue string
 */
export const parseSelectionsFromCurrentValue = (
  currentValue: string | undefined,
  processedOptions: { value: string; label: string }[],
  allOptionValue: string,
  otherOptions: { value: string; label: string }[]
): Set<string> => {
  if (!currentValue || currentValue === "All" || currentValue === "") {
    // "All" selected - select all options
    return new Set(processedOptions.map(opt => opt.value));
  }
  
  // Parse comma-separated values from currentValue
  const selectedFromCurrent = currentValue
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  
  const selections = new Set<string>();
  
  // Add "all" if all other options are in the selection
  const otherOptionValues = otherOptions.map(opt => opt.value);
  const allOtherSelected = otherOptionValues.length > 0 && 
    otherOptionValues.every(val => selectedFromCurrent.includes(val));
  
  if (allOtherSelected) {
    selections.add(allOptionValue);
    otherOptions.forEach(opt => selections.add(opt.value));
  } else {
    // Add only the selected individual options
    selectedFromCurrent.forEach(val => {
      const matchingOption = processedOptions.find(opt => opt.value === val || opt.label === val);
      if (matchingOption) {
        selections.add(matchingOption.value);
      }
    });
  }
  
  // If no selections found, default to "all"
  if (selections.size === 0) {
    return new Set(processedOptions.map(opt => opt.value));
  }
  
  return selections;
};

/**
 * Get RAG (Red-Amber-Green) color based on percentage value.
 * Ranges: Under 74% = Red, 75–89% = Amber, 90% and above = Green.
 */
export const getRAGColor = (value: any, ragColoringColumns?: number[]): string | null => {
  if (!ragColoringColumns || ragColoringColumns.length === 0) {
    return null;
  }

  let percentage: number;
  if (typeof value === 'string') {
    const cleanedValue = value.replace('%', '').replace(',', '').replace(' ', '').trim();
    percentage = parseFloat(cleanedValue);
  } else if (typeof value === 'number') {
    percentage = value;
  } else {
    return null;
  }

  if (isNaN(percentage)) {
    return null;
  }

  if (percentage < 74) {
    return '#EF4444';
  } else if (percentage >= 75 && percentage < 90) {
    return '#EAB000';
  } else if (percentage >= 90) {
    return '#7BD389';
  } else {
    return '#EF4444';
  }
};

/**
 * Transform data for RAG coloring in bar charts
 */
export const transformDataForRAGColoring = (
  data: any[],
  ragColoringColumns: number[],
  isBarChart: boolean,
  getRAGColorFn: (value: any) => string | null
): any[] => {
  if (!isBarChart || !ragColoringColumns || ragColoringColumns.length === 0 || !data || data.length === 0) {
    return data;
  }

  const newData: any[] = JSON.parse(JSON.stringify(data));
  
  const sortedRagColumns = [...ragColoringColumns].sort((a, b) => b - a);
  
  sortedRagColumns.forEach((colIndex) => {
    const styleColumnsAfter = sortedRagColumns.filter(idx => idx > colIndex).length;
    
    const actualDataColumnIndex = colIndex + styleColumnsAfter;
    
    const styleColumnIndex = actualDataColumnIndex + 1;
    
    if (newData[0] && Array.isArray(newData[0])) {
      newData[0].splice(styleColumnIndex, 0, { role: 'style' } as any);
    }
    
    for (let i = 1; i < newData.length; i++) {
      if (newData[i] && Array.isArray(newData[i])) {
        const value = newData[i][actualDataColumnIndex];
        const color = getRAGColorFn(value);
        const styleValue = color ? `fill-color: ${color}` : '';
        newData[i].splice(styleColumnIndex, 0, styleValue);
      }
    }
  });
  
  return newData;
};

/**
 * Check if chart data is valid
 */
export const hasValidData = (
  rows?: any[],
  columns?: any[],
  data?: any[]
): boolean => {
  return !!(
    (rows && rows.length > 0 && columns && columns.length > 0) ||
    (data && data.length > 1)
  );
};

/**
 * Format a single column header (convert camelCase to Title Case / UPPERCASE)
 */
export const formatColumnHeader = (col: any): any => {
  if (!col || typeof col !== 'string') return col;
  return col.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
};

/**
 * Format column headers (convert camelCase to Title Case).
 * Mutates data[0] in place — prefer formatColumnHeader for display-only use.
 */
export const formatColumnHeaders = (data: any[]): void => {
  if (data && data.length > 0 && data[0] !== undefined) {
    data[0] = (data[0] as any[]).map((col) =>
      typeof col === 'string' ? formatColumnHeader(col) : col
    );
  }
};

/**
 * Get display text for dropdown button
 */
export const getDropdownDisplayText = (
  selectedValues: Set<string>,
  allOptionValue: string,
  otherOptions: { value: string; label: string }[]
): string => {
  const isAllSelected = selectedValues.has(allOptionValue);
  
  if (isAllSelected) {
    return 'All';
  }
  
  const selectedCount = Array.from(selectedValues).filter(v => v !== allOptionValue).length;
  if (selectedCount === 0) {
    return 'None';
  }
  if (selectedCount === otherOptions.length) {
    return 'All';
  }
  
  // For RAG status, show "R A G" format
  const ragMap: { [key: string]: string } = {
    'Red': 'R',
    'Amber': 'A',
    'Green': 'G'
  };
  
  // Get selected option labels/values and map to RAG format
  const selectedRagParts: string[] = [];
  Array.from(selectedValues)
    .filter(v => v !== allOptionValue)
    .forEach(value => {
      // Check if it's a RAG status value
      if (ragMap[value]) {
        selectedRagParts.push(ragMap[value]);
      } else {
        // Check if it matches any option label
        const option = otherOptions.find(opt => opt.value === value || opt.label === value);
        if (option && ragMap[option.label]) {
          selectedRagParts.push(ragMap[option.label]);
        } else if (option && ragMap[option.value]) {
          selectedRagParts.push(ragMap[option.value]);
        }
      }
    });
  
  // Sort to always show in R-A-G order
  const ragOrder = ['R', 'A', 'G'];
  selectedRagParts.sort((a, b) => {
    const aIndex = ragOrder.indexOf(a);
    const bIndex = ragOrder.indexOf(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
  
  // If we have RAG format, use it; otherwise fall back to count
  return selectedRagParts.length > 0 ? selectedRagParts.join(' ') : `${selectedCount} selected`;
};
